import React, { useEffect, useState } from "react";
import { useConfig } from "@/context/ConfigContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api from "@/lib/api";
import { ExternalLink, Wifi, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";

const Row = ({ label, value, mono = true, badge }) => (
  <div className="grid grid-cols-12 gap-4 py-3 border-b border-[var(--border)] last:border-b-0">
    <div className="col-span-4 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">{label}</div>
    <div className={`col-span-8 ${mono ? "mono" : ""} text-sm flex items-center gap-2`}>
      {value || <span className="text-[var(--text-disabled)]">— not set —</span>}
      {badge}
    </div>
  </div>
);

const Status = () => {
  const { config, refresh } = useConfig();
  const [detectedIp, setDetectedIp] = useState("");
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  const detect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.get("/config/detect-ip");
      if (data.ip) {
        setDetectedIp(data.ip);
        toast.success(`Server outbound IP: ${data.ip}`);
      } else {
        toast.error("IP detection failed");
      }
    } catch (e) {
      toast.error("IP detection failed");
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Setup"
        title="Status"
        description="Read-only view of API credentials. All values come from environment variables set on the host."
        actions={
          <Button
            data-testid="btn-refresh-status"
            onClick={refresh}
            variant="outline"
            className="rounded-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        }
      />
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 rounded-sm shadow-none border-[var(--border)]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                  Namecheap configuration
                </div>
                {config?.configured ? (
                  <Badge className="rounded-sm bg-green-600 text-white" data-testid="status-configured">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Configured
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="rounded-sm" data-testid="status-not-configured">
                    <AlertCircle className="w-3.5 h-3.5 mr-1" /> Incomplete
                  </Badge>
                )}
              </div>

              <Row label="ApiUser" value={config?.api_user} />
              <Row label="UserName" value={config?.username} />
              <Row label="ApiKey" value={config?.api_key_masked} />
              <Row
                label="ClientIp"
                value={config?.client_ip}
                badge={
                  config?.client_ip && (
                    <Badge variant="outline" className="rounded-sm text-[10px]">whitelisted</Badge>
                  )
                }
              />
              <Row
                label="Environment"
                value={config?.sandbox ? "SANDBOX" : "PRODUCTION"}
                mono={false}
                badge={
                  <Badge
                    variant="outline"
                    className={`rounded-sm text-[10px] ${config?.sandbox ? "border-yellow-500 text-yellow-700" : "border-green-600 text-green-700"}`}
                  >
                    {config?.sandbox ? "api.sandbox.namecheap.com" : "api.namecheap.com"}
                  </Badge>
                }
              />
              <Row label="Source" value={config?.source || "env"} mono={false} />
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="rounded-sm shadow-none border-[var(--border)] bg-[#FAFAFA]">
              <CardContent className="p-6 space-y-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
                  Server outbound IP
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                  Whitelist this IP in your Namecheap dashboard so the API accepts your requests.
                </p>
                <div className="flex items-center gap-2">
                  <code className="mono px-3 py-2 bg-white border border-[var(--border)] rounded-sm text-sm flex-1">
                    {detectedIp || "—"}
                  </code>
                  <Button
                    data-testid="btn-detect-ip"
                    onClick={detect}
                    disabled={detecting}
                    variant="outline"
                    className="rounded-sm"
                  >
                    <Wifi className="w-4 h-4 mr-2" /> Detect
                  </Button>
                </div>
                <a
                  href="https://ap.www.namecheap.com/settings/tools/apiaccess/"
                  target="_blank"
                  rel="noreferrer"
                  className="brand-text text-sm inline-flex items-center gap-1.5 hover:underline"
                >
                  Open Namecheap API panel <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </CardContent>
            </Card>

            {!config?.configured && (
              <Alert className="rounded-sm border-[var(--border)] bg-white" data-testid="alert-incomplete">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Set the missing env vars on the host and restart:
                  <code className="mono block mt-1 text-xs">
                    NAMECHEAP_API_USER, NAMECHEAP_API_KEY,<br/>NAMECHEAP_USERNAME, NAMECHEAP_CLIENT_IP
                  </code>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Status;
